import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { compose } from 'recompose';
import { graphql } from 'react-apollo';
import { withRouter } from 'react-router-dom';
import {
  Message, Confirm, Dropdown, Dimmer, Loader,
} from 'semantic-ui-react';
import { connect } from 'react-redux';
import _, { isEmpty } from 'lodash';

import withPagination from './WithPagination';
import LoadingDataEnhancer from './LoadingData';
import pendingGuaranteesQuery from '../graphql/pendingGuaranteesQuery';
import PendingGuaranteesList from '../views/PendingGuarantees/List';
import PendingGuaranteeDetails from '../views/PendingGuarantees/Details';
import RejectPendingForm from '../views/PendingGuarantees/RejectPendingForm';
import BulkRejectPendingForm from '../views/PendingGuarantees/BulkRejectPendingForm';
import rejectPendingOfferMutation from '../graphql/rejectPendingOfferMutation';
import bulkRejectPendingOfferMutation from '../graphql/bulkRejectPendingOfferMutation';
import allowPendingOfferMutation from '../graphql/allowPendingOfferMutation';
import bulkAllowPendingOfferMutation from '../graphql/bulkAllowPendingOfferMutation';
import updateOdsAttributesMutation from '../graphql/updateOdsAttributesMutation';
import Search from '../views/Shared/Search';
import { clearSearch, setSearch } from '../data/searchField/actions';
import { setRefreshSearch } from '../data/refreshSearch/actions';
import { setPage } from '../data/page/actions';
import { selectPendingGuaranteesProductFamily } from '../data/pendingGuarantees/selectors';
import GuaranteePanel from '../components/GuaranteePanel';
import ProductSelector from '../components/ProductSelector';
import { GUARANTEE_PRODUCT_LIST, PRODUCT_FAMILY_PAGE_OFFSET_STYLE } from '../constants/productFamily';
import {
  useResetPendingGuaranteesProductFamilyOnExit,
  usePendingGuaranteesProductFamily,
  usePendingGuaranteesProductFamilyTheme,
} from '../hooks/usePendingGuaranteesProductFamily';

const rowMenuOptions = [
  { key: 1, text: 'Allow', value: 'Allow' },
  { key: 2, text: 'Invalidate', value: 'Invalidate' },
];

const dropDownAlign = {
  float: 'right',
  position: 'relative',
  zIndex: 10,
};

const LIST_LOAD_ERROR = 'Could not load pending guarantee offers. Change the product or search again to retry.';

class PendingGuarantees extends Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedPendingGuarantee: null,
      isDetailsModalOpen: false,
      isPendingRejectModalOpen: false,
      formSubmitting: false,
      isConfirmationOpen: false,
      error: null,
      flashMessage: null,
      bulkSelectedPendingOfferIds: [],
      isBulkPendingRejectModalOpen: false,
      bulkFormSubmitting: false,
      bulkSelectedGuaranteePurchaseIds: [],
      pendingGuarantees: [],
      column: '',
      direction: 'descending',
      isProductFamilyLoading: false,
      listLoadFailed: false,
    };
    this.requestedProductFamily = props.data.variables.productFamily;
    this.productFamilyRequestId = 0;

    const {
      clearSearchFields,
      dispatchRefreshSearch,
      refreshSearch,
      data,
    } = props;

    if (refreshSearch) {
      dispatchRefreshSearch(false);
      clearSearchFields({});
      const variableArguments = {
        vin: null,
        dealerNumbers: [],
      };
      data.refetch({
        ...variableArguments,
      });
    }
  }

  // creating an array of state with guarantees which will update the state.
  bulkSelectHandler = (guaranteeOfferId, guaranteePurchaseId) => {
    const { bulkSelectedPendingOfferIds, bulkSelectedGuaranteePurchaseIds } = this.state;
    if (bulkSelectedPendingOfferIds.includes(guaranteeOfferId)) {
      // remove the existing guarantee offer id from bulkSelectedPendingOfferIds
      const localCopyOffer = bulkSelectedPendingOfferIds;
      const values = localCopyOffer.indexOf(guaranteeOfferId);
      localCopyOffer.splice(values, 1);
      this.setState({ bulkSelectedPendingOfferIds: localCopyOffer });
      // remove the guarantee purchase id from the bulkSelectedGuaranteePurchaseIds
      const localCopyGuarantee = bulkSelectedGuaranteePurchaseIds;
      const value = localCopyGuarantee.indexOf(guaranteePurchaseId);
      localCopyGuarantee.splice(value, 1);
      this.setState({ bulkSelectedGuaranteePurchaseIds: localCopyGuarantee });
    } else {
      this.setState({
        bulkSelectedPendingOfferIds: [
          ...bulkSelectedPendingOfferIds,
          guaranteeOfferId,
        ],
      });
      this.setState({
        bulkSelectedGuaranteePurchaseIds: [
          ...bulkSelectedGuaranteePurchaseIds,
          guaranteePurchaseId,
        ],
      });
    }
  };

  handleSort = clickedColumn => () => {
    const { column, pendingGuarantees, direction } = this.state;
    if (column !== clickedColumn) {
      this.setState({
        column: clickedColumn,
        pendingGuarantees: _.sortBy(pendingGuarantees, [clickedColumn]),
        direction: 'ascending',
      });
      return;
    }

    const en = Object.assign([], pendingGuarantees);
    const dir = direction === 'ascending' ? 'descending' : 'ascending';
    this.setState({
      pendingGuarantees: en.reverse(),
      direction: dir,
    });
  };

  componentDidMount() {
    const {
      data: { pendingGuarantees },
    } = this.props;
    this.setState({ pendingGuarantees });
    this.syncProductFamily();
  }

  // newProps got the latest props and this.props got only the old data
  componentDidUpdate(prevProps) {
    const {
      data: { pendingGuarantees },
    } = this.props;
    if (pendingGuarantees !== prevProps.data.pendingGuarantees) {
      this.setState({ pendingGuarantees });
    }
    this.syncProductFamily();
  }

  syncProductFamily = () => {
    const { productFamily } = this.props;
    if (productFamily !== this.requestedProductFamily) {
      this.requestedProductFamily = productFamily;
      this.refetchForProductFamily(productFamily);
    }
  };

  refetchForProductFamily = productFamily => {
    const { data, setPage: setPageProp } = this.props;
    this.productFamilyRequestId += 1;
    const requestId = this.productFamilyRequestId;
    const isLatest = () => requestId === this.productFamilyRequestId;
    this.setState({
      isProductFamilyLoading: true,
      listLoadFailed: false,
      bulkSelectedPendingOfferIds: [],
      bulkSelectedGuaranteePurchaseIds: [],
    });
    data.refetch({ productFamily })
      .then(() => {
        if (!isLatest()) return;
        this.setState({ listLoadFailed: false });
        setPageProp({});
      })
      .catch(() => {
        if (isLatest()) this.setState({ listLoadFailed: true });
      })
      .finally(() => {
        if (isLatest()) this.setState({ isProductFamilyLoading: false });
      });
  };

  onSelectGuarantee = (action, guarantee) => {
    this.setState({
      selectedPendingGuarantee: guarantee,
    });

    switch (action) {
      case 'Details':
        this.setState({
          isDetailsModalOpen: true,
        });
        break;
      case 'Invalidate':
        this.setState({
          isPendingRejectModalOpen: true,
        });
        break;
      case 'Allow':
        this.setState({
          isConfirmationOpen: true,
        });
        break;
      case 'UpdateOdsAttributes':
        this.updateOdsAttributes(guarantee);
        break;
      default:
        this.setState({
          selectedPendingGuarantee: null,
        });
    }
  };

  onPendingRejectFormSubmitHandler = values => {
    this.setState({
      formSubmitting: true,
    });
    const { rejectPendingMutation, data } = this.props;
    const rejectInput = { ...values };
    rejectPendingMutation({
      variables: { rejectInput },
    })
      .then(() => {
        data.refetch().then(({ data: responseData }) => {
          this.setState({ pendingGuarantees: responseData.pendingGuarantees });
        });
        this.setState({
          isPendingRejectModalOpen: false,
          formSubmitting: false,
          isDetailsModalOpen: false,
          flashMessage: 'Successfully Invalidated Guarantee Offer',
        });
      })
      .catch(() => {
        this.setState({
          formSubmitting: false,
          error: 'Error while Invalidating Guarantee Offer',
        });
      });
  };

  onBulkPendingRejectFormSubmitHandler = values => {
    this.setState({
      bulkFormSubmitting: true,
    });
    const { bulkRejectPendingMutation, data } = this.props;
    const input = { ...values };
    bulkRejectPendingMutation({
      variables: { input },
    })
      .then(() => {
        data.refetch().then(({ data: responseData }) => {
          this.setState({ pendingGuarantees: responseData.pendingGuarantees });
        });
        this.setState({
          isBulkPendingRejectModalOpen: false,
          bulkSelectedPendingOfferIds: [],
          bulkSelectedGuaranteePurchaseIds: [],
          bulkFormSubmitting: false,
          flashMessage: 'Successfully Invalidated Guarantee Offers',
        });
      })
      .catch(() => {
        this.setState({
          bulkFormSubmitting: false,
          error: 'Error while Invalidating Guarantee Offers',
        });
      });
  };

  handleConfirmationClose = () => {
    this.setState({ isConfirmationOpen: false });
  };

  handleConfirm = () => {
    this.allowPendingGuarantee();
  };

  handleMessageDismiss = () => {
    this.setState({ flashMessage: null });
  };

  onDetailsModalCloseHandler = () => {
    this.setState({
      isDetailsModalOpen: false,
      selectedPendingGuarantee: null,
    });
  };

  onPendingRejectFormCloseHandler = () => {
    this.setState({
      isPendingRejectModalOpen: false,
    });
  };

  onBulkPendingRejectFormCloseHandler = () => {
    this.setState({
      isBulkPendingRejectModalOpen: false,
    });
  };

  invalidatePendingGuarantee = () => {
    this.setState({
      isPendingRejectModalOpen: true,
    });
  };

  allowPendingGuarantee = () => {
    const { allowMutation, data, setPage: setPageAction } = this.props;
    const { selectedPendingGuarantee } = this.state;
    const guaranteeOfferId = selectedPendingGuarantee.id;
    const input = { guaranteeOfferId };

    this.setState({
      formSubmitting: true,
      isConfirmationOpen: false,
    });

    allowMutation({
      variables: { input },
    })
      .then(({ data: responseData }) => {
        data.refetch().then(({ data: refetchData }) => {
          this.setState({ pendingGuarantees: refetchData.pendingGuarantees });
        });

        const { allowPendingOffer: success } = responseData;
        const msg = success
          ? 'Successfully Validated Guarantee Offer'
          : 'Error while Validating Guarantee Offer';
        this.setState({
          isDetailsModalOpen: false,
          formSubmitting: false,
          flashMessage: msg,
        });
        setPageAction({});
      })
      .catch(() => {
        this.setState({
          formSubmitting: false,
          error: 'Error while Validating Guarantee Offer',
        });
      });
  };

  bulkAllowPendingGuarantee = () => {
    // for allow, needs only the bulkSelectedPendingOfferIds, it has the guarantee offer ids
    const { bulkSelectedPendingOfferIds } = this.state;
    const { bulkAllowMutation, data, setPage: setPageAction } = this.props;
    const input = { guaranteeOfferIds: bulkSelectedPendingOfferIds };

    bulkAllowMutation({
      variables: { input },
    })
      .then(() => {
        data.refetch().then(({ data: refetchData }) => {
          this.setState({ pendingGuarantees: refetchData.pendingGuarantees });
        });
        this.setState({
          bulkSelectedPendingOfferIds: [],
          bulkSelectedGuaranteePurchaseIds: [],
          flashMessage: 'Successfully Validated Guarantee Offers',
        });
        setPageAction({});
      })
      .catch(() => {
        this.setState({
          error: 'Error while Validating Guarantee Offer',
        });
      });
  };

  updateOdsAttributes = guarantee => {
    const { updateOdsMutation } = this.props;
    const input = { guaranteeOfferId: guarantee.id };

    this.setState({
      formSubmitting: true,
    });

    updateOdsMutation({
      variables: { input },
    })
      .then(() => {
        this.setState({
          formSubmitting: false,
          flashMessage: 'Successfully updated ods attributes',
        });
      })
      .catch(() => {
        this.setState({
          formSubmitting: false,
          error: 'Error while updating ods attributes',
        });
      });
  };

  onSearchSubmitHandler = event => {
    const {
      data,
      setSearchFields: setSearchFieldsAction,
      setPage: setPageAction,
    } = this.props;
    const { vin, dealerNumbers, locationInitial } = event.target;
    const dealerNos = isEmpty(dealerNumbers.value)
      ? null
      : [dealerNumbers.value];
    const variableArguments = {
      vin: vin.value || null,
      locationInitial: locationInitial.value || null,
      dealerNumbers: dealerNos,
    };

    this.setState({ formSubmitting: true });

    data
      .refetch({ ...variableArguments })
      .then(({ data: responseData }) => {
        this.setState({
          pendingGuarantees: responseData.pendingGuarantees,
          listLoadFailed: false,
        });
      })
      .catch(() => {
        this.setState({ listLoadFailed: true });
      })
      .finally(() => {
        this.setState({ formSubmitting: false });
      });

    setSearchFieldsAction(variableArguments);
    setPageAction({});
  };

  handleDropdownChange = (e, { value }) => {
    e.preventDefault();
    const { bulkSelectedPendingOfferIds } = this.state;
    if (bulkSelectedPendingOfferIds.length === 0) {
      this.setState({
        error: 'Minimum 1 offer will be selected to continue',
        flashMessage: '',
      });
    } else {
      this.setState({ flashMessage: '', error: '' });
      switch (value) {
        case 'Invalidate':
          this.setState({
            isBulkPendingRejectModalOpen: true,
          });
          break;
        case 'Allow':
          this.bulkAllowPendingGuarantee();
          break;
        default:
          this.setState({
            bulkSelectedGuaranteePurchaseIds: [],
            bulkSelectedPendingOfferIds: [],
          });
      }
    }
  };

  render() {
    const { searchFields, productFamily } = this.props;
    const {
      selectedPendingGuarantee,
      isDetailsModalOpen,
      error,
      isPendingRejectModalOpen,
      formSubmitting,
      flashMessage,
      isBulkPendingRejectModalOpen,
      bulkFormSubmitting,
      bulkSelectedGuaranteePurchaseIds,
      pendingGuarantees,
      direction,
      column,
      isConfirmationOpen,
      isProductFamilyLoading,
      listLoadFailed,
    } = this.state;

    return (
      <div>
        {flashMessage && (
          <Message
            onDismiss={this.handleMessageDismiss}
            content={flashMessage}
            positive
          />
        )}
        {error && (
          <Message negative>
            <p>{error}</p>
          </Message>
        )}
        <Search
          onSubmit={this.onSearchSubmitHandler}
          searchFields={searchFields}
          searchOptions={['dealerNumbers', 'vin', 'locationInitial']}
        />
        <div style={dropDownAlign}>
          <Dropdown
            text='Bulk Actions'
            options={rowMenuOptions}
            onChange={this.handleDropdownChange}
            selectOnBlur={false}
            value={null}
          />
        </div>
        <Dimmer.Dimmable dimmed={isProductFamilyLoading}>
          <Dimmer active={isProductFamilyLoading} inverted>
            <Loader data-testid='list-loading-overlay'>Loading...</Loader>
          </Dimmer>
          {listLoadFailed ? (
            <Message negative data-testid='list-load-error' content={LIST_LOAD_ERROR} />
          ) : (
            <PendingGuaranteesList
              key={productFamily}
              onSelectGuarantee={this.onSelectGuarantee}
              pendingGuarantees={pendingGuarantees}
              direction={direction}
              column={column}
              bulkSelectHandler={this.bulkSelectHandler}
              isEligibleForBulkUpdate={this.isEligibleForBulkUpdate}
              handleSort={this.handleSort}
            />
          )}
        </Dimmer.Dimmable>
        {selectedPendingGuarantee && isDetailsModalOpen && (
          <PendingGuaranteeDetails
            submitting={formSubmitting}
            onSelectGuarantee={this.onSelectGuarantee}
            onCloseHandler={this.onDetailsModalCloseHandler}
            pendingGuarantee={selectedPendingGuarantee}
            isDetailsModalOpen={isDetailsModalOpen}
            invalidatePendingGuarantee={this.invalidatePendingGuarantee}
            allowPendingGuarantee={this.allowPendingGuarantee}
          />
        )}
        {selectedPendingGuarantee && isPendingRejectModalOpen && (
          <div>
            <RejectPendingForm
              isPendingRejectModalOpen={isPendingRejectModalOpen}
              submitting={formSubmitting}
              onCloseHandler={this.onPendingRejectFormCloseHandler}
              onSubmitHandler={this.onPendingRejectFormSubmitHandler}
              pendingGuarantee={selectedPendingGuarantee}
              error={error}
            />
          </div>
        )}
        {isBulkPendingRejectModalOpen && (
          <div>
            <BulkRejectPendingForm
              isBulkPendingRejectModalOpen={isBulkPendingRejectModalOpen}
              submitting={bulkFormSubmitting}
              onCloseHandler={this.onBulkPendingRejectFormCloseHandler}
              onSubmitHandler={this.onBulkPendingRejectFormSubmitHandler}
              bulkSelectedGuaranteePurchaseIds={
                bulkSelectedGuaranteePurchaseIds
              }
              error={error}
            />
          </div>
        )}
        <Confirm
          open={isConfirmationOpen}
          onCancel={this.handleConfirmationClose}
          onConfirm={this.handleConfirm}
        />
      </div>
    );
  }
}

PendingGuarantees.propTypes = {
  clearSearchFields: PropTypes.func.isRequired,
  dispatchRefreshSearch: PropTypes.func.isRequired,
  refreshSearch: PropTypes.bool,
  data: PropTypes.shape({
    refetch: PropTypes.func.isRequired,
    pendingGuarantees: PropTypes.array,
    variables: PropTypes.shape({
      productFamily: PropTypes.string,
    }).isRequired,
  }).isRequired,
  productFamily: PropTypes.string.isRequired,
  rejectPendingMutation: PropTypes.func.isRequired,
  bulkRejectPendingMutation: PropTypes.func.isRequired,
  allowMutation: PropTypes.func.isRequired,
  setPage: PropTypes.func.isRequired,
  bulkAllowMutation: PropTypes.func.isRequired,
  updateOdsMutation: PropTypes.func.isRequired,
  setSearchFields: PropTypes.func.isRequired,
  searchFields: PropTypes.object,
};

const mapDispatchToProps = dispatch => ({
  setSearchFields: fields => {
    dispatch(setSearch(fields));
  },
  dispatchRefreshSearch: value => {
    dispatch(setRefreshSearch(value));
  },
  clearSearchFields: () => {
    dispatch(clearSearch());
  },
  setPage: () => {
    dispatch(setPage());
  },
});

const mapStateToProps = state => {
  const {
    data: { searchFields, refreshSearch },
  } = state;
  return {
    searchFields,
    refreshSearch,
    productFamily: selectPendingGuaranteesProductFamily(state),
  };
};

const PendingGuaranteesContainer = compose(
  withRouter,
  withPagination(pendingGuaranteesQuery, 'pendingGuarantees', {}, state => ({
    productFamily: selectPendingGuaranteesProductFamily(state),
  })),
  graphql(rejectPendingOfferMutation, { name: 'rejectPendingMutation' }),
  graphql(bulkRejectPendingOfferMutation, {
    name: 'bulkRejectPendingMutation',
  }),
  graphql(allowPendingOfferMutation, { name: 'allowMutation' }),
  graphql(bulkAllowPendingOfferMutation, { name: 'bulkAllowMutation' }),
  graphql(updateOdsAttributesMutation, { name: 'updateOdsMutation' }),
  LoadingDataEnhancer,
  connect(mapStateToProps, mapDispatchToProps)
)(PendingGuarantees);

const PendingGuaranteesProductSelector = () => {
  const [selectedProduct, handleProductChange] = usePendingGuaranteesProductFamily();
  const themeClass = usePendingGuaranteesProductFamilyTheme();

  return (
    <ProductSelector
      products={GUARANTEE_PRODUCT_LIST}
      selectedProduct={selectedProduct}
      onProductChange={handleProductChange}
      tooltipContent='Select the product to filter pending guarantee offers.'
      themeClass={themeClass}
    />
  );
};

const PendingGuaranteesTheme = ({ children }) => {
  const themeClass = usePendingGuaranteesProductFamilyTheme();
  return (
    <div data-testid='pending-guarantees-theme' className={`theme ${themeClass}`} style={PRODUCT_FAMILY_PAGE_OFFSET_STYLE}>
      {children}
    </div>
  );
};

PendingGuaranteesTheme.propTypes = {
  children: PropTypes.node.isRequired,
};

const PendingGuaranteesPage = props => {
  useResetPendingGuaranteesProductFamilyOnExit();
  return (
    <PendingGuaranteesTheme>
      <GuaranteePanel>
        <PendingGuaranteesProductSelector />
      </GuaranteePanel>
      {React.createElement(PendingGuaranteesContainer, props)}
    </PendingGuaranteesTheme>
  );
};

export default PendingGuaranteesPage;
